import { type CreatePackageFlightRequest, type PackageFlightOut } from '@utazzo/shared';
import { UzDateTimePicker, UzForm, UzFormField, UzInput } from '@utazzo/ui';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { PackageFormPanel } from './PackageFormPanel';

interface FormValues {
  sectorFrom: string | null;
  sectorTo: string | null;
  flightNumber: string | null;
  /** Combined date + time; split back into the API's date/time strings on submit. */
  departure: Date | null;
  arrival: Date | null;
}

/** Combine the API's `YYYY-MM-DD` date + free-text time into a single Date. */
const combineDateTime = (date: string | null, time: string | null): Date | null => {
  if (!date) return null;
  const [y, m, d] = date.split('-').map((n) => Number(n));
  if (!y || !m || !d) return null;
  let hours = 0;
  let minutes = 0;
  const match = time?.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (match) {
    hours = Number(match[1]);
    minutes = Number(match[2]);
    const mer = match[3]?.toUpperCase();
    if (mer === 'PM' && hours < 12) hours += 12;
    if (mer === 'AM' && hours === 12) hours = 0;
  }
  return new Date(y, m - 1, d, hours, minutes, 0, 0);
};

const toDateStr = (dt: Date): string =>
  `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
const toTimeStr = (dt: Date): string =>
  `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;

interface Props {
  mode: 'create' | 'edit';
  item?: PackageFlightOut;
  pending: boolean;
  onClose: () => void;
  onSubmit: (payload: CreatePackageFlightRequest) => Promise<void>;
}

const toDefaults = (mode: 'create' | 'edit', item: PackageFlightOut | undefined): FormValues =>
  mode === 'edit' && item
    ? {
        sectorFrom: item.sectorFrom,
        sectorTo: item.sectorTo,
        flightNumber: item.flightNumber,
        departure: combineDateTime(item.departureDate, item.departureTime),
        arrival: combineDateTime(item.arrivalDate, item.arrivalTime),
      }
    : {
        sectorFrom: null,
        sectorTo: null,
        flightNumber: null,
        departure: null,
        arrival: null,
      };

const trimOrNull = (v: string | null | undefined): string | null => {
  if (v === null || v === undefined) return null;
  const t = v.trim();
  return t === '' ? null : t;
};

export const PackageFlightFormDrawer = ({
  mode,
  item,
  pending,
  onClose,
  onSubmit,
}: Props): React.JSX.Element => {
  const form = useForm<FormValues>({ defaultValues: toDefaults(mode, item) });

  useEffect(() => {
    form.reset(toDefaults(mode, item));
  }, [mode, item, form]);

  const submit = form.handleSubmit(async (values) => {
    await onSubmit({
      sectorFrom: trimOrNull(values.sectorFrom),
      sectorTo: trimOrNull(values.sectorTo),
      flightNumber: trimOrNull(values.flightNumber),
      departureDate: values.departure ? toDateStr(values.departure) : null,
      departureTime: values.departure ? toTimeStr(values.departure) : null,
      arrivalDate: values.arrival ? toDateStr(values.arrival) : null,
      arrivalTime: values.arrival ? toTimeStr(values.arrival) : null,
    });
  });

  return (
    <PackageFormPanel
      title={mode === 'create' ? 'Add flight' : 'Edit flight'}
      description="Flight sector and schedule details for this package."
      pending={pending}
      submitLabel={mode === 'create' ? 'Add flight' : 'Save changes'}
      onClose={onClose}
      onSubmit={submit}
    >
      <UzForm form={form} onSubmit={submit}>
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <UzFormField name="sectorFrom">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                  label="From"
                  placeholder="e.g. DEL"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>

            <UzFormField name="sectorTo">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                  label="To"
                  placeholder="e.g. DXB"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>

          <UzFormField name="flightNumber">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label="Flight number"
                placeholder="e.g. EK512"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <UzFormField name="departure">
              {({ field, error }) => (
                <UzDateTimePicker
                  label="Departure"
                  placeholder="Pick date & time"
                  value={(field.value as Date | null) ?? null}
                  onValueChange={(v) => field.onChange(v)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>

            <UzFormField name="arrival">
              {({ field, error }) => (
                <UzDateTimePicker
                  label="Arrival"
                  placeholder="Pick date & time"
                  value={(field.value as Date | null) ?? null}
                  onValueChange={(v) => field.onChange(v)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>
        </div>
      </UzForm>
    </PackageFormPanel>
  );
};
